import { FC, lazy, ReactElement, useEffect, useState } from 'react';
import './App.scss';
import Layout from './components/layout/Layout';
import {
  createBrowserRouter,
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import NotFound from './pages/NotFound';
import AuthenticatedRoute from './components/auth/AuthenticatedRoute';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch } from './store/hooks';
import { appSettingsActions } from './features/app-settings/app-settings-slice';
import { ssoSilent } from './features/auth/msal-instance';
import { userProfileActions } from './features';
import { CustomNavigationClient } from './utils/NavigationClient';
import { store } from './store';
import { Provider } from 'react-redux';
import { RouteConfig } from './routes';
import GlobalLoader from './components/common/GlobalLoader/GlobalLoader';
import FirmwareDetailPage from './pages/firmware/FirmwareDetail/FirmwareDetailPage';
import { ConfirmDialogProvider } from './components/common/ConfirmDialog/ConfirmDialogContext';
import IntercomInfoPage from './pages/intercoms/IntercomDetail/IntercomInfoPage';
import { UserProfile } from './model/user-profile';
import initMsalInstance from './features/auth/msal-instance';
import { IAppSettings } from './model/app-settings';
import { ErrorBoundary } from './components/common/error-boundary/ErrorBoundary';

export type Router = ReturnType<typeof createBrowserRouter>;

const HomePage = lazy(() => import('./pages/home/HomePage'));
const FirmwarePage = lazy(() => import('./pages/firmware/FirmwarePage'));
const IntercomsPage = lazy(() => import('./pages/intercoms/IntercomsPage'));
const AuditLog = lazy(() => import('./pages/auditLog/AuditLogPage'));

const createAppRouter = (appElement: ReactElement) => {
  const routeConfig = RouteConfig();
  return createHashRouter(
    createRoutesFromElements(
      <Route
        element={appElement}
        errorElement={<NotFound />}
        handle={{
          crumb: () => 'k-i-home',
        }}
      >
        <Route element={<AuthenticatedRoute requireAuth={false} redirectPath='/intercoms' />}>
          <Route path={routeConfig.Home.path} element={<HomePage />} />
          <Route path='/login' element={<HomePage />} />

          <Route path='*' element={<Navigate to='/login' replace />} />
        </Route>

        <Route element={<AuthenticatedRoute requireAuth={true} redirectPath='/' />}>
          <Route path={routeConfig.Intercoms.path} element={<IntercomsPage key='Intercoms-Page' />} />
          <Route path={routeConfig.Intercoms.children[0].path} element={<IntercomInfoPage key='IntercomInfo-Page' />} />
          <Route path={routeConfig.Firmware.path} element={<FirmwarePage key='Firmware-Page' />} />
          <Route path={routeConfig.Firmware.children[0].path} element={<FirmwareDetailPage key='FirmwareDetail-Page' />} />
          <Route path={routeConfig.AuditLogs.path} element={<AuditLog key='AuditLog-Page' />} />
        </Route>

        <Route path='*' element={<NotFound />} />
      </Route>,
    ),
  );
};

const App = () => {
  const history = useNavigate();
  const { instance } = useMsal();

  useEffect(() => {
    const navigationClient = new CustomNavigationClient(history);
    instance.setNavigationClient(navigationClient);
  }, [history, instance]);

  return (
    <>
      <Layout>
        <Outlet />
      </Layout>

      <ToastContainer autoClose={2000} position='bottom-right' />
    </>
  );
};

const AppWrapper: FC<{ settings: IAppSettings }> = ({ settings }) => {
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const [router, setRouter] = useState<Router | undefined>();

  useEffect(() => {
    dispatch(appSettingsActions.fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    initMsalInstance(instance, (account) => {
      if (account) {
        const userProfile = new UserProfile(account.username, account.name || '', account.idTokenClaims?.roles || []);
        dispatch(userProfileActions.setProfile(userProfile));
      }
    })
      .then((msal) => ssoSilent(msal).then((res) => msal))
      .then((msal) => {
        setRouter(createAppRouter(<App />));
        return msal;
      });
  }, [dispatch, instance, settings]);

  if (!router) {
    return <div></div>;
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ConfirmDialogProvider>
          <RouterProvider router={router} />
        </ConfirmDialogProvider>
        <GlobalLoader></GlobalLoader>
      </Provider>
    </ErrorBoundary>
  );
};

export default AppWrapper;
